import React, { useState } from 'react';
import { InputNumber, Input, Select, Button, Space, Card, Table, Empty, Checkbox } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { usePW9314Query } from './hooks/usePW9314Query';
import type { LeaveSummaryRow } from '../../services/pw9314Api';
import './PW9314Form.css';

const { Option } = Select;

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);
const DEPT_LEVEL_OPTIONS = [
  { value: 1, label: '部' },
  { value: 2, label: '處' },
  { value: 3, label: '課' },
  { value: 4, label: '組' },
];

const PW9314Form: React.FC = () => {
  const { loading, data, leaveTypes, queried, noData, runQuery, reset } = usePW9314Query();

  const currentYear = new Date().getFullYear();
  const [yearFrom, setYearFrom] = useState<number>(currentYear);
  const [monthFrom, setMonthFrom] = useState<number>(1);
  const [monthTo, setMonthTo] = useState<number>(12);
  const [deptFrom, setDeptFrom] = useState('');
  const [deptTo, setDeptTo] = useState('');
  const [deptLevel, setDeptLevel] = useState<number>(3);
  const [unapproved, setUnapproved] = useState(false);

  const handleQuery = () => {
    runQuery({
      yearFrom: yearFrom || undefined,
      monthFrom,
      monthTo,
      deptFrom: deptFrom.trim() || undefined,
      deptTo: deptTo.trim() || undefined,
      deptLevel,
      unapproved,
    });
  };

  const handleReset = () => {
    setYearFrom(currentYear);
    setMonthFrom(1);
    setMonthTo(12);
    setDeptFrom('');
    setDeptTo('');
    setDeptLevel(3);
    setUnapproved(false);
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;
    const leaveHeaders = leaveTypes
      .map(lt => `<th>${lt.name}</th>`)
      .join('');
    const leaveRows = data.map(r => {
      const cells = leaveTypes
        .map((lt, idx) => `<td class="right">${r.leave_hours[idx + 1] || 0}</td>`)
        .join('');
      return `<tr>
        <td>${r.emp_code}</td>
        <td>${r.emp_name}</td>
        <td>${r.year_mon}</td>
        <td>${r.dept_code}</td>
        <td>${r.dept_name}</td>
        ${cells}
        <td class="right">${r.h_tot}</td>
        <td class="right">${r.d_tot}</td>
      </tr>`;
    }).join('');
    win.document.write(`<!DOCTYPE html><html><head><title>月份請假總表</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:11px}h2{text-align:center}
table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:2px 5px}
th{background:#f0f0f0;text-align:center}.right{text-align:right}
@media print{body{margin:8mm}}</style></head><body>
<h2>月份請假總表</h2>
<table><thead><tr>
<th>員工代碼</th><th>姓名</th><th>年月</th><th>部門代碼</th><th>部門名稱</th>
${leaveHeaders}<th>總時數</th><th>總天數</th>
</tr></thead><tbody>${leaveRows}</tbody></table>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  // Build dynamic columns from leave types returned by API
  const leaveColumns: ColumnsType<LeaveSummaryRow> = leaveTypes.map((lt, idx) => ({
    title: lt.name,
    key: `leave_${lt.code}`,
    width: 70,
    align: 'right' as const,
    render: (_: unknown, record: LeaveSummaryRow) => {
      const val = record.leave_hours[idx + 1];
      return val ? val : '';
    },
  }));

  const columns: ColumnsType<LeaveSummaryRow> = [
    { title: '員工代碼', dataIndex: 'emp_code', key: 'emp_code', width: 90 },
    { title: '姓名', dataIndex: 'emp_name', key: 'emp_name', width: 80 },
    { title: '年月', dataIndex: 'year_mon', key: 'year_mon', width: 80 },
    { title: '部門代碼', dataIndex: 'dept_code', key: 'dept_code', width: 90 },
    { title: '部門名稱', dataIndex: 'dept_name', key: 'dept_name', width: 120, ellipsis: true },
    ...leaveColumns,
    { title: '總時數', dataIndex: 'h_tot', key: 'h_tot', width: 75, align: 'right' as const },
    { title: '總天數', dataIndex: 'd_tot', key: 'd_tot', width: 75, align: 'right' as const },
  ];

  const rowKey = (r: LeaveSummaryRow) => `${r.emp_code}_${r.year_mon}`;

  return (
    <PermissionGuard module={Modules.PW9314} action="query">
      <div className="pw9314-container">
        <div className="pw9314-header">
          <h2>月份請假總表</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>

        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap size="middle">
            <div>
              <label className="pw9314-label">年份</label>
              <InputNumber
                value={yearFrom}
                onChange={v => setYearFrom(v ?? currentYear)}
                min={2000}
                max={2099}
                style={{ width: 90 }}
              />
            </div>
            <div>
              <label className="pw9314-label">起始月份</label>
              <Select value={monthFrom} onChange={setMonthFrom} style={{ width: 80 }}>
                {MONTH_OPTIONS.map(m => (
                  <Option key={m} value={m}>{m} 月</Option>
                ))}
              </Select>
            </div>
            <div>
              <label className="pw9314-label">結束月份</label>
              <Select value={monthTo} onChange={setMonthTo} style={{ width: 80 }}>
                {MONTH_OPTIONS.map(m => (
                  <Option key={m} value={m}>{m} 月</Option>
                ))}
              </Select>
            </div>
            <div>
              <label className="pw9314-label">部門代碼起</label>
              <Input
                value={deptFrom}
                onChange={e => setDeptFrom(e.target.value)}
                placeholder="起始部門"
                style={{ width: 120 }}
                allowClear
              />
            </div>
            <div>
              <label className="pw9314-label">部門代碼迄</label>
              <Input
                value={deptTo}
                onChange={e => setDeptTo(e.target.value)}
                placeholder="結束部門"
                style={{ width: 120 }}
                allowClear
              />
            </div>
            <div>
              <label className="pw9314-label">部門層級</label>
              <Select value={deptLevel} onChange={setDeptLevel} style={{ width: 80 }}>
                {DEPT_LEVEL_OPTIONS.map(o => (
                  <Option key={o.value} value={o.value}>{o.label}</Option>
                ))}
              </Select>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 2 }}>
              <Checkbox
                checked={unapproved}
                onChange={e => setUnapproved(e.target.checked)}
              >
                含未核准
              </Checkbox>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleQuery}
                  loading={loading}
                >
                  查詢
                </Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>

        {queried && noData && (
          <Empty description="查無此資料" style={{ marginTop: 40 }} />
        )}
        {queried && !noData && (
          <Table<LeaveSummaryRow>
            columns={columns}
            dataSource={data}
            rowKey={rowKey}
            loading={loading}
            size="small"
            pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
            scroll={{ x: 900 }}
          />
        )}
      </div>
    </PermissionGuard>
  );
};

export default PW9314Form;
